import { ReactNode, useEffect, useRef, useState } from "react";
import "./showcase.css";

export type ShowcaseItem = {
  /** Anchor id (jump links) and React key. */
  id: string;
  title: string;
  /** Text under the title: description, route line, meta line… */
  body: ReactNode;
  /** Decorative visual shown on the sticky stage (desktop) or under the text. */
  visual: ReactNode;
};

type ShowcaseProps = {
  items: ShowcaseItem[];
  /** Accessible name of the section, e.g. "Our services". */
  label: string;
  /** Appended to the last item's number, e.g. "↺". */
  endSuffix?: string;
};

/**
 * Scroll-driven showcase:
 * - left: items as stations on a vertical brand track; the track fills coral
 *   as you scroll, the current station lights up, the rest fade back
 * - right: one sticky stage; the current item's visual flips up into place
 *   while the previous one tilts away (3D deck), behind it a huge outlined
 *   step number and a caption
 * Under 900px it's a plain stack with each visual under its text.
 */
const Showcase = ({ items, label, endSuffix }: ShowcaseProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = String(items.length).padStart(2, "0");

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const steps: HTMLElement[] = Array.from(list.querySelectorAll<HTMLElement>("[data-step]"));
    let frame = 0;

    const update = () => {
      frame = 0;
      const mid = window.innerHeight * 0.5;
      // Active step = the last one whose top has passed the middle of the screen.
      let next = 0;
      steps.forEach((el, i) => {
        if (el.getBoundingClientRect().top <= mid) next = i;
      });
      setActive(next);
      // Track fill = how far the middle of the screen is through the list.
      const box = list.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (mid - box.top) / box.height));
      list.style.setProperty("--is-showcase-progress", p.toFixed(4));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [items.length]);

  return (
    <section className="is-container is-showcase" aria-label={label}>
      <div className="is-showcase__list" ref={listRef}>
        <span className="is-showcase__track" aria-hidden="true">
          <span className="is-showcase__fill" />
        </span>
        {items.map((item, i) => {
          const last = i === items.length - 1;
          const state = i === active ? " is-active" : i < active ? " is-past" : "";
          return (
            <article
              key={item.id}
              id={item.id}
              data-step
              className={"is-showcase__step" + (last ? " is-showcase__step--end" : "") + state}
              aria-labelledby={`showcase-${item.id}`}
            >
              <span className="is-showcase__station" aria-hidden="true" />
              <div className="is-showcase__head">
                <span className={"is-showcase__num" + (last ? " is-showcase__num--accent" : "")}>
                  {String(i + 1).padStart(2, "0")} / {total}
                  {last && endSuffix ? ` ${endSuffix}` : ""}
                </span>
                <h2 id={`showcase-${item.id}`} className="is-display is-showcase__title">
                  {item.title}
                </h2>
              </div>
              <div className="is-showcase__body">{item.body}</div>
              {/* Phones: visual sits under the text instead of on the stage. */}
              <div className="is-showcase__inline" aria-hidden="true">
                {item.visual}
              </div>
            </article>
          );
        })}
      </div>

      <div className="is-showcase__stage" aria-hidden="true">
        <span className="is-showcase__index">{String(active + 1).padStart(2, "0")}</span>
        {items.map((item, i) => (
          <div
            key={item.id}
            className={
              "is-showcase__slide" +
              (i === active ? " is-active" : i < active ? " is-past" : " is-next")
            }
          >
            {item.visual}
          </div>
        ))}
        <span className="is-showcase__caption">
          <span className="is-showcase__caption-dot" />
          {items[active]?.title}
        </span>
      </div>
    </section>
  );
};

export default Showcase;
